import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../lib/supabase';
import { useInvites } from '../../hooks/useInvites';
import Button from '../../components/Button/Button';
import Input from '../../components/Input/Input';
import SignUpLayout from '../SignUp/SignUpLayout';
import '../SignUp/SignUp.css';
import './OtpVerification.css';

const OtpVerification = () => {
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [email, setEmail] = useState('');
  const [isSignUp, setIsSignUp] = useState(false);
  const { verifyOtp, user, signIn } = useAuth();
  const navigate = useNavigate();
  const [canResend, setCanResend] = useState(false);
  const [resendTimer, setResendTimer] = useState(60); // 60 second cooldown
  
  // Use the invites hook to check for invites
  useInvites();

  // Check if user has a profile
  const checkUserProfile = async (userId: string): Promise<boolean> => {
    try {
      const { data, error } = await supabase
        .from('Profiles')
        .select('id')
        .eq('user_id', userId)
        .single();
        
      if (error && error.code === 'PGRST116') { // No rows returned
        return false;
      }
      
      return !!data; // Return true if profile exists
    } catch (err) {
      return false;
    }
  };

  // Check if user has a team
  const checkUserTeam = async (userId: string): Promise<boolean> => {
    try {
      // Check if user is in the users array of any team in the Teams table
      const { data: teamsData, error: teamsError } = await supabase
        .from('Teams')
        .select('id, users')
        .contains('users', [userId]);
      
      if (!teamsError && teamsData && teamsData.length > 0) {
        return true;
      }
      
      // Try alternative approach - get all teams and check manually
      const { data: allTeams, error: allTeamsError } = await supabase
        .from('Teams')
        .select('id, users');
        
      if (!allTeamsError && allTeams && allTeams.length > 0) {
        // Check each team's users array for the userId
        const userTeams = allTeams.filter(team => 
          team.users && Array.isArray(team.users) && team.users.includes(userId)
        );
        
        if (userTeams.length > 0) {
          return true;
        }
      }
      
      // Check if user has created any teams
      const { data: createdTeams, error: createdTeamsError } = await supabase
        .from('Teams')
        .select('id')
        .eq('owner_profile_id', userId);
        
      if (!createdTeamsError && createdTeams && createdTeams.length > 0) {
        return true;
      }
      
      // User has no teams
      return false;
    } catch (err) {
      return false;
    }
  };

  // Determine next step based on user's profile and team status
  const determineNextStep = async (userId: string) => {
    // Check if this is part of the sign-up flow
    const signUpFlag = localStorage.getItem('isSignUp');
    
    if (signUpFlag !== 'true') {
      // Not a sign-up flow, go directly to dashboard
      navigate('/overview');
      return;
    }
    
    // Always send new signups to user info step
    navigate('/user-info');
    return;
  };

  useEffect(() => {
    // Redirect if already signed in
    if (user) {
      // If this is a returning user with a profile, clear the sign-up flag
      // to ensure they don't go through the sign-up flow again
      const checkAndClearSignUpFlag = async () => {
        const hasProfile = await checkUserProfile(user.id);
        const hasTeam = await checkUserTeam(user.id);
        
        // If user has both profile and team, they're a returning user
        if (hasProfile && hasTeam) {
          localStorage.removeItem('isSignUp');
        }
        
        determineNextStep(user.id);
      };
      
      checkAndClearSignUpFlag();
      return;
    }

    // Get email from localStorage
    const storedEmail = localStorage.getItem('userEmail');
    if (storedEmail) {
      setEmail(storedEmail);
    } else {
      navigate('/login');
    }

    // Check if this is part of the sign-up flow
    const signUpFlag = localStorage.getItem('isSignUp');
    setIsSignUp(signUpFlag === 'true');
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run on user/navigate only
  }, [user, navigate]);

  // Set up resend timer
  useEffect(() => {
    setCanResend(false);
    let timer: NodeJS.Timeout;
    
    if (resendTimer > 0) {
      timer = setTimeout(() => {
        setResendTimer(prev => prev - 1);
      }, 1000);
    } else {
      setCanResend(true);
    }
    
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [resendTimer]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    
    // Only allow numbers and limit to 6 digits
    if (!/^\d*$/.test(value)) return;
    if (value.length <= 6) {
      setOtp(value);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    if (otp.length !== 6) {
      setError('Please enter all 6 digits');
      setLoading(false);
      return;
    }

    try {
      await verifyOtp(email, otp);
      
      // User is now authenticated, determineNextStep will be called in the useEffect
    } catch (err: any) {
      setError(err.message || 'Invalid OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (!canResend) return;
    
    try {
      setCanResend(false);
      setResendTimer(60); // Reset timer to 60 seconds
      
      await signIn(email);
      
      setError('');
      // Reset OTP field
      setOtp('');
    } catch (err: any) {
      setError(err.message || 'Failed to resend OTP');
    }
  };

  return (
    <SignUpLayout
      title="Verification Code"
      subtitle={`We've sent a code to ${email}`}
      currentStep={isSignUp ? 2 : 1}
      totalSteps={isSignUp ? 3 : 1}
      footer={
        <div className="otp-resend">
          Didn't receive a code?{' '}
          {canResend ? (
            <span 
              className="otp-resend-link"
              onClick={handleResendOtp}
            >
              Resend
            </span>
          ) : (
            <span className="otp-resend-timer">
              Resend in {resendTimer}s
            </span>
          )}
        </div>
      }
    >
      <form onSubmit={handleSubmit} className="signup-form">
        <div className="signup-form-fields">
          <Input
            label="Verification Code"
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={6}
            value={otp}
            onChange={handleChange}
            placeholder="Enter 6-digit code"
            error={error}
            sizeVariant="large"
            required
          />
        </div>
        
        <Button 
          type="submit" 
          className="signup-button"
          variant="primary"
          size="large"
          disabled={loading || otp.length !== 6}
          isLoading={loading}
        >
          Verify
        </Button>
      </form>
    </SignUpLayout>
  );
};

export default OtpVerification; 